import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { canvasserApi } from '../lib/api'
import {
  cacheWalkList,
  getCachedWalkList,
  cacheScript,
  getCachedScript,
  getLocalVisitStatuses,
  CacheExpiredError,
  localDateKey,
  markStopDoneHere,
  getStopsDoneHere,
  getPlacementFlips,
  getRouteOutcomes,
  type LocalVisitState,
} from '../lib/offlineStore'
import { applyPlacementFlips } from '../lib/placementOverlay'
import { applyRouteOutcomes } from '../lib/routeOutcomeOverlay'
import { isNetworkError, serverMessage } from '../lib/syncEngine'
import { toaster } from '@/components/ui/toaster'
import type {
  AddCanvasserNoteRequest,
  MyTurfAssignmentDto,
  AddToTurfRequest,
} from '../types/canvasser'

// ─── Helpers ─────────────────────────────────────────────────────

/** Merge local visit status overrides onto turf data */
async function mergeLocalStatuses(
  turfs: MyTurfAssignmentDto[]
): Promise<MyTurfAssignmentDto[]> {
  const localStatuses = await getLocalVisitStatuses()
  if (localStatuses.size === 0) return turfs

  // [CF-1 P5, R4] Chase stops are per-assignment: a chase-route stop reads only a knock recorded on
  // that turf, and a chase knock never ticks a canvass stop. Canvass turfs keep the old rule (any
  // non-chase knock on the person counts; a pre-P5 record without `turfs` is a canvass knock).
  const chaseTurfIds = new Set(turfs.filter(t => t.assignmentKind === 'ChaseRoute').map(t => t.turfId))
  const overlayFor = (turf: MyTurfAssignmentDto, local: LocalVisitState): boolean | null => {
    const own = local.turfs?.[turf.turfId]
    if (turf.assignmentKind === 'ChaseRoute') return own ?? null
    if (!local.turfs) return local.hasComeBackLater
    const canvassKnocks = Object.entries(local.turfs).filter(([id]) => !chaseTurfIds.has(id))
    if (canvassKnocks.length === 0) return null
    return own ?? canvassKnocks.some(([, cbl]) => cbl)
  }

  return turfs.map(turf => {
    // [CF-1 P3] localVisitStatus is keyed by contactId across every turf; a queued DOOR visit must not
    // tick the same person's sign-route stop. Action routes get their own overlay (applyRouteOutcomes).
    if (turf.assignmentKind === 'ActionRoute') return turf
    const contacts = turf.contacts.map(contact => {
      const local = localStatuses.get(contact.contactId)
      const hasComeBackLater = local ? overlayFor(turf, local) : null
      if (hasComeBackLater !== null) {
        return { ...contact, isVisited: true, hasComeBackLater }
      }
      return contact
    })
    return {
      ...turf,
      contacts,
      visitedCount: contacts.filter(c => c.isVisited).length,
    }
  })
}

// ─── Turf Assignments (offline-aware) ────────────────────────────

export function useMyTurfs(campaignId: string | undefined, options?: { pollWhileEmptyMs?: number }) {
  const poll = options?.pollWhileEmptyMs
  return useQuery({
    queryKey: ['canvasser', 'turfs', campaignId],
    queryFn: async () => {
      let turfs: MyTurfAssignmentDto[]
      try {
        turfs = await canvasserApi.getMyTurfs().then(r => r.data)
        // Cache for offline use (keyed by the grant's campaign)
        await cacheWalkList(campaignId!, turfs)
      } catch (err) {
        // Fall back to cached data on network error
        if (isNetworkError(err)) {
          const cached = await getCachedWalkList(campaignId!)
          if (cached.status === 'hit') {
            turfs = cached.data
          } else if (cached.status === 'expired') {
            throw new CacheExpiredError() // [LEGAL] 7-day TTL — surface "reconnect to refresh"
          } else {
            throw err
          }
        } else {
          throw err
        }
      }
      // Overlay locally-recorded visit statuses, then today's "Visit at home today" flips (Phase 4), then queued action-route outcomes (CF-1 P3).
      return applyRouteOutcomes(
        applyPlacementFlips(await mergeLocalStatuses(turfs), await getPlacementFlips()),
        await getRouteOutcomes(),
      )
    },
    enabled: !!campaignId,
    // [Phase 1 / spec §4.2] The waiting room polls while there is no turf yet, and refetches on
    // focus, so an assignment shows up without a reload. Off for every other observer.
    refetchInterval: poll ? (query) => (query.state.data?.length === 0 ? poll : false) : false,
    // Focus refetch only while WAITING (empty list): 'always' bypasses the global staleTime; once
    // there is turf the home's observer stays mounted mid-walk and must not refetch on every focus.
    refetchOnWindowFocus: poll ? (query) => (query.state.data?.length === 0 ? 'always' : false) : false,
    // Once the browser has gone offline, a fresh query must still run so it reaches the
    // IndexedDB fallback above, instead of pausing with no data and no error.
    networkMode: 'offlineFirst',
  })
}

// ─── Script Resolution (offline-aware) ───────────────────────────

export function useResolveScript(
  campaignId: string | undefined,
  contactId: string | undefined,
  turfId: string | undefined
) {
  return useQuery({
    queryKey: ['canvasser', 'script', campaignId, contactId, turfId],
    queryFn: async () => {
      try {
        const script = await canvasserApi
          .resolveScript(contactId!, turfId!)
          .then(r => r.data)
        // Cache for offline use
        await cacheScript(campaignId!, contactId!, turfId!, script)
        return script
      } catch (err) {
        if (isNetworkError(err)) {
          const cached = await getCachedScript(campaignId!, contactId!, turfId!)
          if (cached.status === 'hit') return cached.data
          if (cached.status === 'expired') throw new CacheExpiredError()
        }
        throw err
      }
    },
    enabled: !!campaignId && !!contactId && !!turfId,
    networkMode: 'offlineFirst', // reach the offline-cache fallback even after going offline
  })
}

// ─── Contact Detail (grant-authorized canvasser endpoint, read-only) ───
// [CANV-6] Reads the grant-authorized /api/canvassing/contacts/{id}/detail, NOT the organizer-only
// /api/contacts/{id}/detail. The organizer endpoint is JWT-only; a roleless grant session 401s there,
// and the api.ts 401 interceptor misroutes that 401 to the organizer /login instead of the canvasser
// redeem screen. The canvasser endpoint is GrantOnly and turf-gated, and returns a minimal projection.

export function useCanvasserContactDetail(contactId: string | undefined) {
  return useQuery({
    queryKey: ['canvasser', 'contact', contactId, 'detail'],
    queryFn: () => canvasserApi.getContactDetail(contactId!).then(r => r.data),
    enabled: !!contactId,
    staleTime: 60 * 1000,
    // [Phase 5 P5-6] The Info sheet never spins: offline, fail at once (the sheet shows "More details need
    // a connection") instead of pausing with no data and no error. Try again is the user's call.
    networkMode: 'offlineFirst',
    retry: false,
  })
}

// ─── Campaign Tags ──────────────────────────────────────────────

export function useCampaignTags(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['canvasser', 'campaign-tags', campaignId],
    queryFn: () => canvasserApi.getCampaignTags().then(r => r.data),
    enabled: !!campaignId,
    networkMode: 'offlineFirst', // fail fast offline rather than pausing with no data/no error
  })
}

// ─── Canvasser Notes ─────────────────────────────────────────────

export function useAddCanvasserNote(contactId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AddCanvasserNoteRequest) =>
      canvasserApi.addNote(contactId, data).then(r => r.data),
    onError: (err: unknown) => {
      toaster.create({
        title: serverMessage(err, 'That contact is no longer on your walk list'),
        type: 'error',
        duration: 4000,
        closable: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvasser', 'contact', contactId, 'detail'] })
    },
  })
}

// ─── Canvasser Tag Toggle ────────────────────────────────────────

export function useCanvasserAssignTag(contactId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tagId: string) => canvasserApi.assignTag(contactId, tagId),
    onError: (err: unknown) => {
      toaster.create({
        title: serverMessage(err, 'That contact is no longer on your walk list'),
        type: 'error',
        duration: 4000,
        closable: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvasser', 'contact', contactId, 'detail'] })
    },
  })
}

export function useCanvasserRemoveTag(contactId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tagId: string) => canvasserApi.removeTag(contactId, tagId),
    onError: (err: unknown) => {
      toaster.create({
        title: serverMessage(err, 'That contact is no longer on your walk list'),
        type: 'error',
        duration: 4000,
        closable: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvasser', 'contact', contactId, 'detail'] })
    },
  })
}

// ─── Do Not Contact ─────────────────────────────────────────

export function useSetDoNotContact(contactId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => canvasserApi.setDoNotContact(contactId),
    onError: (err: unknown) => {
      toaster.create({
        title: serverMessage(err, 'That contact is no longer on your walk list'),
        type: 'error',
        duration: 4000,
        closable: true,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvasser', 'contact', contactId, 'detail'] })
      queryClient.invalidateQueries({ queryKey: ['canvasser', 'turfs'] })
    },
  })
}

// ─── Add to Turf ────────────────────────────────────────────────

export function useAddToTurf() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: AddToTurfRequest) =>
      canvasserApi.addToTurf(request).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvasser', 'turfs'] })
    },
  })
}

/** [§15.1 S4] Today's done-here worksite stops for a turf (local IndexedDB only — no network). */
export function useStopsDoneHere(turfId: string | undefined) {
  return useQuery({
    queryKey: ['canvasser', 'doneHere', turfId, localDateKey()],
    queryFn: () => getStopsDoneHere(turfId!),
    enabled: !!turfId,
    networkMode: 'always',
    staleTime: Infinity,
  })
}

export function useMarkStopDoneHere() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ turfId, stopKey }: { turfId: string; stopKey: string }) => markStopDoneHere(turfId, stopKey),
    networkMode: 'always',
    // The broad prefix also refreshes Today's ['canvasser','doneHere','all',…] read.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['canvasser', 'doneHere'] }),
  })
}
